/**
 * TripsMapView Component - Shows all trips on a single map
 * Each trip is displayed as a route line with drop markers
 */

import React, { useState, useEffect, useMemo } from 'react';
import { RouteMapWrapper } from '../../../components/RouteMap';
import { Box, Typography, Card, CardContent } from '@mui/material';
import styled from 'styled-components';
import { logError, logInfo } from '../../../shared/utils/logger.js';

const MapContainer = styled(Box)`
  height: 600px;
  min-height: 400px;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e8e8e8;
  background-color: #f5f5f5;
`;

const LoadingContainer = styled(Box)`
  height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
`;

const ErrorContainer = styled(Box)`
  height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
`;

/**
 * Get marker color based on drop status
 */
const getStatusColor = (status) => {
  const colorMap = {
    pending: 'yellow',
    arrived: 'blue',
    completed: 'green',
    problem: 'red',
  };
  return colorMap[status] || 'grey';
};

/**
 * Build address string from drop location
 */
const buildAddress = (location) => {
  if (!location) return '';
  const parts = [];
  if (location.address) parts.push(location.address);
  if (location.city) parts.push(location.city);
  if (location.province) parts.push(location.province);
  return parts.join(', ') || 'Unknown location';
};

/**
 * Geocode an address using Nominatim
 */
const geocodeAddress = async (address) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
      {
        headers: {
          'User-Agent': 'Bubakii Logistics Platform',
        },
      }
    );
    const data = await response.json();
    if (data.length > 0) {
      return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
    }
    return null;
  } catch (err) {
    logError('Geocoding error', {
      address,
      error: err.message,
    });
    return null;
  }
};

/**
 * Calculate route between multiple waypoints using OSRM
 */
const calculateRoute = async (waypoints) => {
  if (waypoints.length < 2) return null;
  
  try {
    const waypointString = waypoints.map(wp => `${wp[1]},${wp[0]}`).join(';');
    const response = await fetch(
      `https://router.project-osrm.org/route/v1/driving/${waypointString}?overview=full&geometries=geojson`
    );
    const data = await response.json();
    
    if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
      return data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);
    }
    return null;
  } catch (err) {
    logError('Route calculation error', {
      waypointsCount: waypoints.length,
      error: err.message,
    });
    return null;
  }
};

/**
 * TripsMapView Component
 * @param {Object} props
 * @param {Array} props.trips - Array of trip objects
 * @param {Object} props.filters - Filter options { status, routeCorridor, showCompleted }
 * @param {Function} props.onTripClick - Callback when trip route is clicked
 * @param {Function} props.onDropClick - Callback when drop marker is clicked
 */
const TripsMapView = ({ 
  trips = [],
  filters = {},
  onTripClick,
  onDropClick,
}) => {
  const [allMarkers, setAllMarkers] = useState([]);
  const [allRoutes, setAllRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter trips based on filters
  const filteredTrips = useMemo(() => {
    let filtered = trips;
    
    if (filters.status) {
      filtered = filtered.filter(trip => trip.status === filters.status);
    }
    
    if (filters.routeCorridor) {
      filtered = filtered.filter(trip => 
        trip.routeCorridor === filters.routeCorridor
      );
    }
    
    return filtered;
  }, [trips, filters]);

  // Get all drops from filtered trips
  const allDrops = useMemo(() => {
    const drops = [];
    filteredTrips.forEach(trip => {
      if (trip.drops && trip.drops.length > 0) {
        trip.drops.forEach(drop => {
          drops.push({
            ...drop,
            tripId: trip.id,
            tripStatus: trip.status,
          });
        });
      }
    });
    
    // Apply drop filters
    let filtered = drops;
    
    if (filters.showCompleted === false) {
      filtered = filtered.filter(drop => drop.status !== 'completed');
    }
    
    return filtered;
  }, [filteredTrips, filters]);

  useEffect(() => {
    const loadMapData = async () => {
      if (filteredTrips.length === 0) {
        setLoading(false);
        setError('No trips to display');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Process all trips and their drops
        const markers = [];
        const routePromises = [];

        for (const trip of filteredTrips) {
          if (!trip.drops || trip.drops.length === 0) continue;

          // Sort drops by sequence
          const sortedDrops = [...trip.drops].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
          
          // Geocode all drops for this trip
          const dropCoords = [];
          for (const drop of sortedDrops) {
            const address = buildAddress(drop.location);
            
            let coords = null;
            if (drop.location?.coordinates?.lat && drop.location?.coordinates?.lng) {
              coords = [drop.location.coordinates.lat, drop.location.coordinates.lng];
            } else {
              coords = await geocodeAddress(address);
            }
            
            if (coords) {
              dropCoords.push({ drop, coords, address });
              
              // Create marker for this drop
              const dropOrders = drop.orders || [];
              const popupContent = (
                <Box>
                  <Typography variant="subtitle2" component="strong" sx={{ display: 'block', mb: 0.5 }}>
                    Trip: {trip.id || 'Unknown'}
                  </Typography>
                  <Typography variant="subtitle2" component="strong" sx={{ display: 'block', mb: 0.5 }}>
                    Drop {drop.sequence || '?'}: {drop.location?.city || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {address}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                    Status: {drop.status?.toUpperCase() || 'PENDING'}
                  </Typography>
                  {dropOrders.length > 0 && (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      Orders: {dropOrders.length}
                    </Typography>
                  )}
                </Box>
              );

              markers.push({
                coords,
                icon: getStatusColor(drop.status),
                label: `Trip ${trip.id} - Drop ${drop.sequence}`,
                popup: popupContent,
                drop,
                trip,
              });
            }
          }

          // Calculate route for this trip if we have at least 2 drops
          if (dropCoords.length >= 2) {
            const waypoints = dropCoords.map(item => item.coords);
            routePromises.push(
              calculateRoute(waypoints).then(route => ({
                tripId: trip.id,
                route,
                color: trip.status === 'completed' ? '#52c41a' : 
                       trip.status === 'en_route' ? '#1890ff' : 
                       '#faad14', // planned/loaded
              }))
            );
          }
        }

        setAllMarkers(markers);

        // Calculate all routes
        const routes = await Promise.all(routePromises);
        setAllRoutes(routes.filter(r => r.route !== null));

        logInfo('TripsMapView loaded successfully', {
          tripsCount: filteredTrips.length,
          dropsCount: markers.length,
          routesCount: routes.length,
        });
      } catch (err) {
        logError('Failed to load trips map', {
          error: err.message,
          tripsCount: filteredTrips.length,
        });
        setError('Error loading map');
      } finally {
        setLoading(false);
      }
    };

    loadMapData();
  }, [filteredTrips, filters]);

  const handleMarkerClick = (marker, index) => {
    if (onDropClick && marker.drop) {
      onDropClick(marker.drop, marker.trip);
    }
  };

  if (loading) {
    return (
      <LoadingContainer>
        <Typography variant="body1">Loading map...</Typography>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
        {filteredTrips.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No trips match the current filters
          </Typography>
        )}
      </ErrorContainer>
    );
  }

  if (allMarkers.length === 0) {
    return (
      <ErrorContainer>
        <Typography variant="body1" color="text.secondary">
          No drops to display
        </Typography>
      </ErrorContainer>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Map View - {filteredTrips.length} Trip{filteredTrips.length !== 1 ? 's' : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Showing {allMarkers.length} drop{allMarkers.length !== 1 ? 's' : ''} across {allRoutes.length} route{allRoutes.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <MapContainer>
          <RouteMapWrapper
            markers={allMarkers}
            routes={allRoutes}
            autoFitBounds={true}
            onMarkerClick={handleMarkerClick}
          />
        </MapContainer>
      </CardContent>
    </Card>
  );
};

export default TripsMapView;

